import { Navigate, useRoutes } from 'react-router-dom';
import { useSelector } from 'react-redux';

// layouts
import PropTypes from 'prop-types';
import DashboardLayout from './layouts/dashboard';
import LogoOnlyLayout from './layouts/LogoOnlyLayout';
import AuthLayout from './layouts/AuthLayout';
//
import NotFound from './pages/Page404';
import PageUnauthorized from './pages/PageUnauthorized';
import Racks from './pages/Racks';
import CreateRack from './pages/CreateRack';
import RackDetails from './pages/RackDetails';
import Policies from './pages/Policies';
import Teams from './pages/Teams';
import Users from './pages/Users';
import VSMConnect from './pages/VSMConnect';
import VSMRemoteView from './pages/VSMRemoteView';
import PageMaintenanceMode from './pages/PageMaintenanceMode';
import VMTemplates from './pages/VMTemplates';
import Snapshots from './pages/Snapshots';

import AdminSettings from './pages/AdminSettings';
import AdminCompanies from './pages/AdminCompanies';
import AdminGlobalAdmins from './pages/AdminGlobalAdmins';
import Billing from './pages/Billing';
import CompanyBillingReport from './pages/CompanyBillingReport';
import TeamUsageReport from './pages/TeamUsageReport';
import TeamPartNumberTracking from './pages/TeamPartNumberTracking';
import CompanyPartNumberDeployments from './pages/CompanyPartNumberDeployments';
import UploadLoadable from './pages/UploadLoadable';
//
import ProtectedRoute from './components/nav/ProtectedRoute';
import { useIsFeatureFlagEnabled } from './utils/FeatureFlags';
import SnapshotDetails from './pages/SnapshotDetails';

// Gates a subtree on a feature flag — renders the children if the flag is
// enabled, otherwise redirects to /404. Used to hide entire route trees
// (sidebar Reporting section and its pages) behind a flag toggle. Wraps
// <ProtectedRoute>, not the other way around, so unauthenticated users
// can't confirm a gated route exists by visiting it logged out.
//
// On a hard refresh the feature-flag fetch is async, so `featureFlags.value`
// is still `[]` on first paint. Without this guard we'd redirect to /404
// before the flags arrive and the user would land on 404 even though they
// have access. We track whether the flags slice has been hydrated at least
// once via `featureFlags.fetched` and render nothing until then — distinct
// from "loaded and disabled", which is the only case that should redirect.
function FeatureFlagRoute({ flag, children }) {
  const enabled = useIsFeatureFlagEnabled(flag);
  const fetched = useSelector((state) => state.featureFlags.fetched);
  if (!fetched) return null;
  if (!enabled) return <Navigate to="/404" replace />;
  return children;
}

FeatureFlagRoute.propTypes = {
  flag: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired
};

// ----------------------------------------------------------------------
export default function Router() {
  const isMaintenanceModeEnabled = useIsFeatureFlagEnabled('maintenance_mode');

  let routes = [
    {
      path: '/racks',
      element: (
        <ProtectedRoute>
          <DashboardLayout />
        </ProtectedRoute>
      ),
      children: [
        { path: '/racks', element: <Racks /> },
        {
          path: '/racks/details/:rackname',
          element: <RackDetails />
        }
      ]
    },
    {
      path: '/teams',
      element: (
        <ProtectedRoute>
          <DashboardLayout>
            <Teams />
          </DashboardLayout>
        </ProtectedRoute>
      )
    },
    {
      path: '/admin',
      element: (
        <ProtectedRoute>
          <DashboardLayout />
        </ProtectedRoute>
      ),
      children: [
        { path: 'policies', element: <Policies /> },
        { path: 'teams', element: <Teams /> },
        { path: 'users', element: <Users /> },
        { path: 'settings', element: <AdminSettings /> },
        { path: 'companies', element: <AdminCompanies /> },
        { path: 'global-admins', element: <AdminGlobalAdmins /> }
      ]
    },
    {
      path: '/billing',
      element: (
        <FeatureFlagRoute flag="company_billing">
          <ProtectedRoute>
            <DashboardLayout />
          </ProtectedRoute>
        </FeatureFlagRoute>
      ),
      children: [
        { path: '', element: <Billing /> },
        { path: 'company/:companyId', element: <CompanyBillingReport /> },
        // Company-scoped team detail — keeps the URL nested under the
        // company so the breadcrumb on the team page preserves the
        // Billing → Company chain. The legacy /team-usage/:teamId route
        // below still serves users who reach the page directly via the
        // sidebar. `team` and `part` are delimiter segments, not entities
        // — the Breadcrumb component excludes them from the trail so they
        // aren't clickable.
        {
          path: 'company/:companyId/team/:teamId',
          element: <TeamUsageReport />
        },
        {
          path: 'company/:companyId/team/:teamId/part/:partNumber',
          element: <TeamPartNumberTracking />
        },
        // Company-scoped deployments for a single part number -- the
        // company billing report's part-numbers table clicks land here.
        // Aggregates deployments across every team for the company; sits
        // alongside the team-scoped deployments route above.
        {
          path: 'company/:companyId/part/:partNumber',
          element: <CompanyPartNumberDeployments />
        }
      ]
    },
    {
      path: '/team-usage',
      element: (
        <FeatureFlagRoute flag="team_usage_report_ui">
          <ProtectedRoute>
            <DashboardLayout />
          </ProtectedRoute>
        </FeatureFlagRoute>
      ),
      children: [
        { path: '', element: <TeamUsageReport /> },
        { path: ':teamId', element: <TeamUsageReport /> },
        {
          path: ':teamId/part/:partNumber',
          element: <TeamPartNumberTracking />
        }
      ]
    },
    {
      path: '/createrack',
      element: (
        <ProtectedRoute>
          <DashboardLayout>
            <CreateRack />
          </DashboardLayout>
        </ProtectedRoute>
      )
    },
    {
      path: '/',
      element: <LogoOnlyLayout />,
      children: [
        { path: '404', element: <NotFound /> },
        { path: '401', element: <PageUnauthorized /> },
        { path: '/', element: <Navigate to="/racks" /> },
        { path: '*', element: <Navigate to="/404" /> },
        { path: 'maintenance', element: <PageMaintenanceMode /> }
      ]
    },
    {
      path: '/login',
      element: <AuthLayout />
    },
    {
      path: '/vsm/:rackname',
      element: (
        <ProtectedRoute>
          <VSMConnect />
        </ProtectedRoute>
      )
    },
    {
      path: '/webrtc/:rackname',
      element: (
        <ProtectedRoute>
          <VSMRemoteView />
        </ProtectedRoute>
      )
    },
    {
      path: '/upload',
      element: (
        <ProtectedRoute>
          <DashboardLayout>
            <UploadLoadable />
          </DashboardLayout>
        </ProtectedRoute>
      )
    },
    {
      path: '/vmtemplates',
      element: (
        <ProtectedRoute>
          <DashboardLayout>
            <VMTemplates />
          </DashboardLayout>
        </ProtectedRoute>
      )
    },
    {
      path: '/snapshots',
      element: (
        <ProtectedRoute>
          <DashboardLayout />
        </ProtectedRoute>
      ),
      children: [
        {
          path: '/snapshots',
          element: <Snapshots />
        },

        {
          path: '/snapshots/details/:snapshotname',
          element: <SnapshotDetails />
        }
      ]
    },

    { path: '*', element: <Navigate to="/404" replace /> }
  ];
  if (isMaintenanceModeEnabled) {
    routes = [{ path: '*', element: <PageMaintenanceMode /> }];
  }
  return useRoutes(routes);
}
